import { useMemo } from 'react';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { getAssetQuery, getTestDataQuery } from 'shared/services/api';
import { ASSET, TEST_DATA } from '@elementary/common-ui/constants/queryKeys';
import {
  AssetData,
  AssetDataResponse,
  TestData,
  TestDataApiResponse,
} from './types';

type Props = {
  id?: string;
  isTest?: boolean;
};

export const useAssetData = ({ id, isTest }: Props) => {
  const selectedEnvId = useSelectedEnv();
  const hasPermission = useEnvPermission();
  const includeTestExecutions = hasPermission('VIEW_TESTS');

  const {
    data,
    isFetching: isAssetFetching,
    isError,
    error,
  } = useGraphqlQuery<AssetDataResponse>({
    query: getAssetQuery,
    queryKey: [selectedEnvId, ASSET, id],
    variables: { id, includeTestExecutions },
    enabled: !!id && !isTest,
    select: (data) => data.asset,
  });

  const {
    data: apiTestData,
    isFetching: isTestFetching,
    isError: isTestError,
    error: testError,
  } = useGraphqlQuery<TestDataApiResponse>({
    query: getTestDataQuery,
    queryKey: [selectedEnvId, TEST_DATA, id],
    variables: { id },
    enabled: !!id && isTest,
  });

  const testData = useMemo((): TestData | undefined => {
    if (!apiTestData?.latestTestExecution) return undefined;

    const { test, execution } = apiTestData.latestTestExecution;

    return {
      id: test.id,
      name: test.name,
      description: test.description ?? undefined,
      status: execution.status,
      executionTimestamp: execution.executionTimestamp,
      query: execution.query ?? undefined,
      path: test.path ?? undefined,
    };
  }, [apiTestData]);

  const assetData = useMemo((): AssetData | undefined => {
    if (!id || !data) return undefined;

    return data;
  }, [data, id]);

  return {
    assetData,
    testData,
    isLoading: isAssetFetching || isTestFetching,
    isError: (isError || isTestError) && !testData,
    error: error || testError,
  };
};
